<script setup>
/* global axios */
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { accountId } = useAccount();

const state = ref(null);
const qrData = ref(null);
const isLoggingOut = ref(false);
const errorMessage = ref(null);
const password = ref('');
const isSubmittingPassword = ref(false);

let stateTimer = null;
let qrTimer = null;

const baseUrl = () =>
  `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/telegram_green_api`;

async function fetchState() {
  try {
    const res = await axios.get(`${baseUrl()}/state`);
    state.value = res.data.state;
  } catch {
    errorMessage.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING'
    );
  }
}

async function fetchQr() {
  try {
    const res = await axios.get(`${baseUrl()}/qr`);
    if (res.data.error) {
      errorMessage.value = t(
        'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING'
      );
      return;
    }
    errorMessage.value = null;
    if (res.data.type === 'alreadyLogged') {
      state.value = 'authorized';
      qrData.value = null;
    } else {
      qrData.value = res.data;
    }
  } catch {
    errorMessage.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING'
    );
  }
}

function startQrPolling() {
  fetchQr();
  qrTimer = setInterval(fetchQr, 10000);
}

function stopQrPolling() {
  clearInterval(qrTimer);
  qrTimer = null;
  qrData.value = null;
}

watch(state, (newState, oldState) => {
  const needsQr = newState === 'notAuthorized' || newState === 'starting';
  if (needsQr && !qrTimer) {
    startQrPolling();
  } else if (!needsQr && qrTimer) {
    stopQrPolling();
  }
  // Once Green-API confirms the password (state moves off pendingPassword),
  // re-enable the form. If the password was wrong Green-API leaves us on
  // pendingPassword and we keep the loader running until the next poll
  // confirms — the watcher fires again on every distinct state value.
  if (oldState === 'pendingPassword' && newState !== 'pendingPassword') {
    isSubmittingPassword.value = false;
  }
});

async function submitPassword() {
  if (!password.value.trim()) return;
  isSubmittingPassword.value = true;
  errorMessage.value = null;
  try {
    const res = await axios.post(`${baseUrl()}/submit_password`, {
      password: password.value,
    });
    if (res.data.error) {
      errorMessage.value = t(
        'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING'
      );
      isSubmittingPassword.value = false;
      return;
    }
    password.value = '';
    // Keep isSubmittingPassword=true until the state watcher detects a
    // transition away from pendingPassword. Green-API needs a beat to
    // process the password; without this guard the input form
    // re-renders blank with the form re-enabled, which looks like the
    // submit didn't go through.
    setTimeout(() => fetchState(), 1500);
    // Safety net: if Green-API rejects the password it stays on
    // pendingPassword and the state watcher never fires (Vue skips
    // identical values), so the loader would hang forever. Force the
    // form back after 8s so the user can retry.
    setTimeout(() => {
      isSubmittingPassword.value = false;
    }, 8000);
  } catch {
    errorMessage.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING'
    );
    isSubmittingPassword.value = false;
  }
}

async function handleLogout() {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.DISCONNECT')
    )
  )
    return;
  isLoggingOut.value = true;
  try {
    const res = await axios.post(`${baseUrl()}/logout`);
    if (res.data.error) {
      useAlert(
        t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING')
      );
    } else {
      setTimeout(() => fetchState(), 1500);
    }
  } catch {
    useAlert(
      t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.ERROR_FETCHING')
    );
  } finally {
    isLoggingOut.value = false;
  }
}

onMounted(() => {
  fetchState();
  stateTimer = setInterval(fetchState, 5000);
});

onUnmounted(() => {
  clearInterval(stateTimer);
  clearInterval(qrTimer);
});
</script>

<template>
  <div
    class="flex flex-col gap-4 rounded-lg border border-n-weak bg-n-solid-1 p-4"
  >
    <p class="text-heading-s font-semibold text-n-slate-12">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.TITLE_TELEGRAM') }}
    </p>

    <div v-if="errorMessage" class="text-body-main text-ruby-500">
      {{ errorMessage }}
    </div>

    <div v-if="state === null" class="text-body-main text-n-slate-11">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.LOADING') }}
    </div>

    <div v-else-if="state === 'authorized'" class="flex flex-col gap-3">
      <div class="flex items-center gap-2">
        <span
          class="i-ri-checkbox-circle-fill text-lg text-green-500"
          aria-hidden="true"
        />
        <span class="text-body-main text-n-slate-12">
          {{
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.AUTHORIZED_TELEGRAM'
            )
          }}
        </span>
      </div>
      <div>
        <NextButton :is-loading="isLoggingOut" danger @click="handleLogout">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.DISCONNECT') }}
        </NextButton>
      </div>
    </div>

    <div v-else-if="state === 'pendingPassword'" class="flex flex-col gap-3">
      <p class="text-body-main text-n-slate-12">
        {{
          $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.PENDING_PASSWORD')
        }}
      </p>
      <div
        v-if="isSubmittingPassword"
        class="flex items-center gap-2 text-body-main text-n-slate-11"
      >
        <span class="i-ri-loader-4-line animate-spin text-lg" />
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.AUTHORIZING') }}
      </div>
      <form v-else class="flex flex-col gap-2" @submit.prevent="submitPassword">
        <input
          v-model="password"
          type="password"
          autocomplete="current-password"
          class="!mb-0"
          :placeholder="
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.PASSWORD_PLACEHOLDER'
            )
          "
          :disabled="isSubmittingPassword"
        />
        <div class="flex items-center gap-2">
          <NextButton
            type="submit"
            solid
            blue
            :is-loading="isSubmittingPassword"
            :disabled="!password.trim()"
          >
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.SUBMIT_PASSWORD'
              )
            }}
          </NextButton>
          <NextButton
            type="button"
            faded
            slate
            :is-loading="isLoggingOut"
            @click="handleLogout"
          >
            {{
              $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.START_OVER')
            }}
          </NextButton>
        </div>
      </form>
    </div>

    <div v-else-if="state === 'blocked'" class="text-body-main text-ruby-500">
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.STATE_BLOCKED') }}
    </div>

    <div
      v-else-if="state === 'sleepMode'"
      class="text-body-main text-yellow-600"
    >
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.STATE_SLEEPING') }}
    </div>

    <div v-else class="flex flex-col gap-3">
      <p v-if="state === 'starting'" class="text-body-main text-n-slate-11">
        {{
          $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.STATE_STARTING')
        }}
      </p>
      <p class="text-label-small text-n-slate-11">
        {{
          $t(
            'INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.SCAN_INSTRUCTION_TELEGRAM'
          )
        }}
      </p>
      <div v-if="qrData">
        <img
          :src="`data:image/png;base64,${qrData.message}`"
          :alt="
            $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.TITLE_TELEGRAM')
          "
          class="h-48 w-48 rounded border border-n-weak"
        />
      </div>
      <div v-else class="text-label-small text-n-slate-11">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.GREEN_API.AUTHORIZATION.LOADING') }}
      </div>
    </div>
  </div>
</template>
